import { useEffect, useRef, useState } from 'react';
import { asset } from './asset';
import ShapeBlur from './ShapeBlur';
import './ShapeBlur.css';

const CONTACT_EMAIL = 'ehsan.faghih72@gmail.com';

const NAV_ITEMS = [
  { id: 'hero', href: '/#hero', icon: 'bi-house', label: 'Home' },
  { id: 'about', href: '/#about', icon: 'bi-person', label: 'About' },
  { id: 'resume', href: '/#resume', icon: 'bi-file-earmark-text', label: 'Resume' },
  { id: 'project-samples', href: '/#project-samples', icon: 'bi-kanban', label: 'Project Samples' },
  { id: 'advisors', href: '/#advisors', icon: 'bi-mortarboard', label: 'Advisors' },
  { id: 'personal-interests', href: '/#personal-interests', icon: 'bi-heart', label: 'Personal Interests' },
  { id: 'contact', href: '/#contact', icon: 'bi-chat-dots', label: 'Direct Message' },
];

function IPortfolioShell({ children, activeSection }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [copyToastKey, setCopyToastKey] = useState(0);
  const scrollAreaRef = useRef(null);
  const copyTimeoutRef = useRef(null);

  const showEmailCopiedToast = () => {
    setCopyToastKey((key) => key + 1);
    setEmailCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setEmailCopied(false), 2500);
  };

  const handleCopyEmail = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const copyWithFallback = () => {
      const textarea = document.createElement('textarea');
      textarea.value = CONTACT_EMAIL;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, CONTACT_EMAIL.length);

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      }

      document.body.removeChild(textarea);
      return copied;
    };

    showEmailCopiedToast();

    if (copyWithFallback()) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(CONTACT_EMAIL).catch(() => {});
    }
  };

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return undefined;

    const updateHint = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 2;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      setShowScrollHint(hasOverflow && !atBottom);
    };

    updateHint();
    el.addEventListener('scroll', updateHint);
    window.addEventListener('resize', updateHint);
    const observer = new ResizeObserver(updateHint);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateHint);
      window.removeEventListener('resize', updateHint);
      observer.disconnect();
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const scrollSidebarDown = () => {
    scrollAreaRef.current?.scrollBy({ top: 140, behavior: 'smooth' });
  };

  return (
    <>
      <header
        id="header"
        className={`header dark-background d-flex flex-column${sidebarCollapsed ? ' header-collapsed' : ''}`}
      >
        <i className="header-toggle d-xl-none bi bi-list" />

        <button
          type="button"
          className="header-collapse-btn d-none d-xl-flex"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setSidebarCollapsed((prev) => !prev)}
        >
          <i className={`bi ${sidebarCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} />
        </button>

        <div className="header-scroll-area" ref={scrollAreaRef}>
          <div className="profile-img">
            <div className="profile-img-frame">
              <img src={asset('img/my-profile-img.jpg')} alt="Ehsan Faghih" className="img-fluid rounded-circle" />
              <ShapeBlur
                className="profile-shape-blur"
                variation={2}
                shapeSize={1}
                roundness={0.5}
                borderSize={0.05}
                circleSize={0.25}
                circleEdge={1}
              />
            </div>
          </div>

          <a href="/#hero" className="logo d-flex align-items-center justify-content-center">
            <h1 className="sitename">Ehsan Faghih</h1>
          </a>

          <div className="social-links-wrap">
            <div className="social-links text-center">
              <button
                type="button"
                className="gmail"
                onClick={handleCopyEmail}
                aria-label="Gmail"
                data-tooltip="Gmail"
              >
                <i className="bi bi-envelope-at-fill" />
              </button>
              <a
                href="https://scholar.google.com/citations?user=1xQoOFYAAAAJ&hl=en&oi=ao"
                className="scholar"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google Scholar"
                data-tooltip="Google Scholar"
              >
                <i className="bi bi-mortarboard-fill" />
              </a>
              <a
                href="https://github.com/efaghih"
                className="github"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                data-tooltip="GitHub"
              >
                <i className="bi bi-github" />
              </a>
              <a
                href="https://www.linkedin.com/in/ehsan-faghih-510650b3/"
                className="linkedin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-tooltip="LinkedIn"
              >
                <i className="bi bi-linkedin" />
              </a>
            </div>
            {emailCopied && (
              <div
                className="email-copy-toast"
                role="status"
                aria-live="polite"
                key={copyToastKey}
              >
                Email copied: {CONTACT_EMAIL}
              </div>
            )}
          </div>

          <nav id="navmenu" className="navmenu">
            <ul>
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.href}
                    className={activeSection === item.id ? 'active' : undefined}
                  >
                    <i className={`bi ${item.icon} navicon`} />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {showScrollHint && (
          <button
            type="button"
            className="sidebar-scroll-hint"
            aria-label="Scroll sidebar down"
            onClick={scrollSidebarDown}
          >
            <i className="bi bi-chevron-down" />
          </button>
        )}
      </header>

      {sidebarCollapsed && (
        <button
          type="button"
          className="header-expand-tab d-none d-xl-flex"
          aria-label="Expand sidebar"
          onClick={() => setSidebarCollapsed(false)}
        >
          <i className="bi bi-chevron-right" />
        </button>
      )}

      <main className={`main${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        {children}
      </main>

      <footer id="footer" className={`footer position-relative light-background${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <div className="container">
          <div className="credits text-center">
            <p>Modified by Ehsan Faghih.</p>
          </div>
        </div>
      </footer>

      <a href="#photo-album" id="scroll-top" className="scroll-top d-flex align-items-center justify-content-center"><i className="bi bi-arrow-up-short" /></a>
      <div id="preloader" className="preloader--simple d-flex align-items-center justify-content-center">
        <img src={`${process.env.PUBLIC_URL}/favicon.ico`} alt="" className="preloader-icon" aria-hidden="true" />
      </div>
    </>
  );
}

export default IPortfolioShell;
